// Human copy for plan-change impact items returned by the backend impact
// engine (functions/payments/shared/impactEngine.js loss/blocking types).

export const LOSS_COPY = {
  bank_integration: (loss) => ({
    icon: "🏦",
    severity: "hard",
    title: `${loss.connectionCount} bank connection${loss.connectionCount !== 1 ? "s" : ""} will be permanently disconnected`,
    detail:
      "Imported transactions stay, but daily sync stops and reconnecting later means re-authenticating at your bank.",
  }),
  saved_packages: (loss) => ({
    icon: "📦",
    severity: "hard",
    title: `${loss.count} saved report package${loss.count !== 1 ? "s" : ""} will stop generating`,
    detail:
      (loss.names?.length ? `Including "${loss.names.slice(0, 2).join('", "')}". ` : "") +
      (loss.activeShares > 0
        ? `${loss.activeShares} active share link${loss.activeShares !== 1 ? "s" : ""} will be deactivated.`
        : "Saved packages require Essentials or higher."),
  }),
  labels: (loss) => ({
    icon: "🏷️",
    severity: "soft",
    title: "Labels become read-only",
    detail: `Your ${loss.labelCount} label${loss.labelCount !== 1 ? "s" : ""} stay on existing transactions, but you can't apply new ones or run By-Label reports.`,
  }),
  ml_auto_categorization: () => ({
    icon: "🤖",
    severity: "soft",
    title: "ML auto-categorization turns off",
    detail: "Existing categorizations are kept; new imports are manual.",
  }),
  ai_bank_rec_beta: (loss) => ({
    icon: "🤖",
    severity: "soft",
    title: "Your AI bank reconciliation beta access ends",
    detail: loss.requestable
      ? "Reconciliation goes back to manual. You can re-request beta access through support on your new plan."
      : "Reconciliation goes back to manual.",
  }),
  payroll: () => ({
    icon: "💵",
    severity: "soft",
    title: "Payroll unavailable",
    detail: "Payroll requires Essentials or higher.",
  }),
  budget_by_fund: () => ({
    icon: "📊",
    severity: "soft",
    title: "Budget by fund becomes read-only",
    detail: "Organization-level budgeting stays available on every plan.",
  }),
  multi_org_reporting: () => ({
    icon: "🏢",
    severity: "soft",
    title: "Multi-org reporting unavailable",
    detail: "Consolidated reports across organizations require Advanced.",
  }),
  linked_accounts: () => ({
    icon: "💱",
    severity: "soft",
    title: "Linked accounts & multi-currency (beta) unavailable",
    detail: "Linked accounts and multi-currency reporting require Advanced.",
  }),
};

export const describeLoss = (loss) =>
  (LOSS_COPY[loss.type] || (() => ({
    icon: "⚠️",
    severity: "soft",
    title: loss.type,
    detail: "",
  })))(loss);

// One-sentence user-facing blurbs for locked-feature upsell surfaces,
// keyed by entitlement key (src/config/entitlements.js). Locked toggles /
// chips across the app import from here so the copy can't drift per surface.
export const FEATURE_UPSELL_COPY = {
  consolidatedReporting:
    "Combine your organization and its child organizations into a single set of reports.",
  linkedAccounts:
    "Link asset accounts across organizations so transfers post to both sets of books automatically.",
  donorManagement:
    "Track donors and donations, generate giving statements, and import from your donation platform.",
  labels:
    "Organize transactions with custom labels for events, departments, projects, and more.",
  payroll:
    "Import payrun exports from your payroll processor, post fund-allocated journal entries automatically, and reconcile bank draws in one click.",
  autoReconciliation:
    "Upload a bank statement and let T3 match and clear transactions automatically.",
  fundBudgeting:
    "Budget each account across funds, not just in total.",
};

// Consolidation blurb with the org's real child count when the hierarchy has
// it ("…its 3 child organizations…"), falling back to the static copy.
export const consolidationUpsellBlurb = (childOrgCount) =>
  childOrgCount > 0
    ? `Combine your organization and its ${childOrgCount} child organization${childOrgCount === 1 ? "" : "s"} into a single set of reports.`
    : FEATURE_UPSELL_COPY.consolidatedReporting;

// Keyed by the plan catalog's stored level keys ("plus" displays as Essentials).
export const PLAN_BLURBS = {
  basic: ["Fund accounting & reports", "Reconciliation & imports", "Donor management"],
  plus: [
    "Everything in Basic, plus:",
    "Bank integration (Plaid)",
    "ML auto-categorization",
    "Labels & label reporting",
    "Saved report packages",
    "Up to 2 integrations",
    // Scrub "coming Fall '26" when PAYROLL_LAUNCHED flips (PayrollHub.jsx)
    "Payroll (coming Fall '26)",
  ],
  advanced: [
    "Everything in Essentials, plus:",
    "Unlimited integrations",
    "Budget by fund",
    "Multi-org reporting",
    "AI bank reconciliation",
    "Linked accounts & multi-currency (beta)",
    "Priority support",
  ],
};

// Tier accent colors for chips/titles (primary is unreadable on white in
// light mode): Basic = grey, Essentials (plus) = secondary, Advanced =
// purple — mode-aware shades.
export const tierAccent = (tier, theme) => {
  if (tier === "advanced") {
    return theme.palette.mode === "dark" ? "#b39ddb" : "#6d4fa3";
  }
  if (tier === "plus" || tier === "essentials") {
    return theme.palette.secondary.main;
  }
  return theme.palette.text.secondary; // basic + anything else: quiet grey
};

export const formatAmount = (cents, currency = "usd") =>
  cents == null
    ? null
    : new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: currency.toUpperCase(),
      }).format(cents / 100);
